import React from 'react';
import { useHistory } from 'react-router-dom';
import './ArtemyStyles.css';
import './GovernanceStyles.css';

function dr(name, pct, color, delay) {
  return (
    <div className="drow">
      <span className="dname">{name}</span>
      <div className="bwrap">
        <div className="bfill" style={{ width: `${pct}%`, background: color, animationDelay: `${delay}s` }}></div>
      </div>
      <span className="dval" style={{ color: color }}>{pct}%</span>
    </div>
  );
}

function mc(name, val, unit, delta, dc, color, svs) {
  const mx = Math.max(...svs);
  const mn = Math.min(...svs);
  const r = mx - mn || 1;
  const pts = svs.map((v, i) => `${i * (120 / (svs.length - 1))},${28 - ((v - mn) / r) * 22}`).join(' ');
  const c = svs[svs.length - 1] >= svs[0] ? '#00d4aa' : '#3d7fff';

  return (
    <div className="mc">
      <div className="mc-top"><div className="mc-name">{name}</div><div className={`delta ${dc}`}>{delta}</div></div>
      <div className="mc-val" style={{ color: color }}>{val}<span className="mc-unit">{unit}</span></div>
      <div className="spark">
        <svg viewBox="0 0 120 30" fill="none">
          <polyline points={pts} fill="none" stroke={c} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>
    </div>
  );
}

function TRow({ cells }) {
  return (
    <tr>
      {cells.map((c, i) => (
        <td key={i} dangerouslySetInnerHTML={{ __html: c }}></td>
      ))}
    </tr>
  );
}

export default function ConfidenceEngine() {
  const history = useHistory();

  return (
    <div className="fi">
      <div className="page-header">
        <div><div className="page-title">Delivery Confidence Score</div><div className="page-sub">System of Truth — composite, explainable, continuously updated probability of on-track delivery</div></div>
        <div className="page-actions"><button className="artemy-btn artemy-btn-ghost" onClick={() => history.push('/control-plane/agent-confidence')}>Agent Confidence →</button><button className="artemy-btn artemy-btn-primary">Configure Weights</button></div>
      </div>

      <div style={{ background: 'rgba(0,212,170,.06)', border: '1px solid rgba(0,212,170,.18)', borderRadius: '12px', padding: '14px 18px', marginBottom: '14px', fontSize: '12.5px', color: 'var(--artemy-text-secondary)', lineHeight: 1.7 }} className="fi1">
        <strong style={{ color: 'var(--artemy-green)', fontFamily: 'var(--artemy-font-display)' }}>System of Truth.</strong>
        Delivery Confidence answers: "Given everything happening across humans, agents, and systems — how confident are we that we will deliver what we intended, securely?" It is a composite, explainable, continuously updated probability score derived exclusively from system-generated signals. It is deterministic, scope-aware, security-aware, and recomputed on every relevant event across the SDLC. Manual input never influences it.
      </div>

      <div className="g-hero fi2" style={{ marginBottom: '14px' }}>
        <div className="conf-card">
          <div className="conf-label">Portfolio Delivery Confidence</div>
          <div className="ring-row">
            <div className="ring-wrap">
              <svg viewBox="0 0 100 100" width="88" height="88"><circle className="ring-bg" cx="50" cy="50" r="45" /><circle className="ring-fill" cx="50" cy="50" r="45" stroke="#00d4aa" style={{ strokeDashoffset: 56 }} /></svg>
              <div className="ring-center"><div className="ring-num" style={{ color: 'var(--artemy-green)' }}>80</div><div className="ring-sub">/ 100</div></div>
            </div>
            <div>
              <div className="spill" style={{ background: 'rgba(0,212,170,.12)', color: 'var(--artemy-green)', border: '1px solid rgba(0,212,170,.25)' }}><span className="dp"></span>GREEN — Confident</div>
              <div style={{ fontFamily: 'var(--artemy-font-display)', fontSize: '14px', fontWeight: 600, marginBottom: '4px' }}>Delivery on track</div>
              <div className="conf-desc">Score derived from 10 weighted dimensions. 1 high-risk dimension (Security Risk: 62%) driving penalties. Trend: +4 pts this week.</div>
            </div>
          </div>
          <div className="dlist">
            {dr('Historical Reliability', 88, 'var(--artemy-green)', .2)}
            {dr('Intent Alignment', 74, 'var(--artemy-yellow)', .3)}
            {dr('Execution Risk', 78, 'var(--artemy-accent)', .4)}
            {dr('Spec Integrity', 86, 'var(--artemy-green)', .45)}
            {dr('Dependency Risk', 62, 'var(--artemy-red)', .5)}
            {dr('Operational Stability', 82, 'var(--artemy-accent)', .55)}
            {dr('Agent Reliability', 95, 'var(--artemy-green)', .6)}
            {dr('Governance Compliance', 90, 'var(--artemy-green)', .65)}
            {dr('Security Risk', 62, 'var(--artemy-red)', .7)}
            {dr('Execution Mix', 81, 'var(--artemy-accent)', .75)}
          </div>
        </div>
        <div>
          <div style={{ display: 'grid', gridTemplateRows: '1fr 1fr', gap: '12px', height: '100%' }}>
            <div className="g2">
              {mc('Signals Processed', '48.3k', 'events', '↑ 12%', 'du', 'var(--artemy-accent)', [20, 35, 28, 45, 38, 52, 46, 60])}
              {mc('Score Recomputations', '1.2k', '/day', 'live', 'df', 'var(--artemy-yellow)', [800, 900, 950, 1100, 1050, 1150, 1180, 1200])}
            </div>
            <div className="g2">
              {mc('Scope Coverage', '11', 'levels', 'full', 'du', 'var(--artemy-green)', [8, 8, 9, 10, 10, 11, 11, 11])}
              {mc('Explainability', '100', '%', 'req.', 'du', 'var(--artemy-green)', [95, 96, 97, 98, 99, 100, 100, 100])}
            </div>
          </div>
        </div>
      </div>

      <div className="gov-card fi2" style={{ marginBottom: '14px' }}>
        <div className="card-header"><div className="card-title">📐 Score Dimension Reference</div><span style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '11px', color: 'var(--artemy-text-dim)' }}>10 weighted dimensions · All required</span></div>
        <table className="dt">
          <thead><tr><th>Dimension</th><th>Score</th><th>Key Inputs</th><th>Status</th></tr></thead>
          <tbody>
            <TRow cells={['Historical Reliability', '<span style="color:var(--artemy-green);font-family:var(--artemy-font-mono)">88%</span>', 'Deploy success rate, change failure rate, incident frequency, MTTR', '<span class="pill pg">Strong</span>']} />
            <TRow cells={['Execution Risk', '<span style="color:var(--artemy-accent);font-family:var(--artemy-font-mono)">78%</span>', 'Change volume, code churn, blast radius, change risk scores', '<span class="pill pb">Watch</span>']} />
            <TRow cells={['Specification Integrity', '<span style="color:var(--artemy-green);font-family:var(--artemy-font-mono)">86%</span>', 'Spec coverage, validation results, drift signals, acceptance criteria completeness', '<span class="pill pg">Strong</span>']} />
            <TRow cells={['Intent Alignment', '<span style="color:var(--artemy-yellow);font-family:var(--artemy-font-mono)">74%</span>', 'PRD→spec→backlog→code linkage, divergence signals, unlinked work', '<span class="pill py">Risk</span>']} />
            <TRow cells={['Dependency Risk', '<span style="color:var(--artemy-red);font-family:var(--artemy-font-mono)">62%</span>', 'Dependency graph depth, volatility, failure propagation likelihood', '<span class="pill pr">Critical</span>']} />
            <TRow cells={['Operational Stability', '<span style="color:var(--artemy-accent);font-family:var(--artemy-font-mono)">82%</span>', 'Incident history, alert frequency, error rates, remediation activity', '<span class="pill pb">Watch</span>']} />
            <TRow cells={['Agent Reliability', '<span style="color:var(--artemy-green);font-family:var(--artemy-font-mono)">95%</span>', 'Historical agent success rate, anomaly detection, rollback frequency, autonomy level', '<span class="pill pg">Strong</span>']} />
            <TRow cells={['Governance Compliance', '<span style="color:var(--artemy-green);font-family:var(--artemy-font-mono)">90%</span>', 'Policy violations, missing approvals, unauthorized actions', '<span class="pill pg">Strong</span>']} />
            <TRow cells={['🔴 Security Risk', '<span style="color:var(--artemy-red);font-family:var(--artemy-font-mono)">62%</span>', 'Policy violations, anomalous access, privilege escalations, prompt injection, dependency CVEs', '<span class="pill pr">Critical</span>']} />
            <TRow cells={['Execution Mix', '<span style="color:var(--artemy-accent);font-family:var(--artemy-font-mono)">81%</span>', 'Human vs automated vs agent-driven action ratio, weighted by risk profile', '<span class="pill pb">Watch</span>']} />
          </tbody>
        </table>
      </div>

      <div className="g3 fi3" style={{ marginBottom: '14px' }}>
        <div className="gov-card" style={{ padding: '16px' }}>
          <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '9.5px', color: 'var(--artemy-text-dim)', letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '12px' }}>Computation Flow</div>
          {[
            ['1', 'Ingest signals', 'Timestamped, source-attributed, immutable once recorded'],
            ['2', 'Normalize dimensions', 'Each dimension produces 0.0–1.0 score independently'],
            ['3', 'Apply time decay', 'Recent signals weighted more heavily than stale data'],
            ['4', 'Apply penalties', 'Policy violations, security risks, instability reduce score'],
            ['5', 'Apply boosts', 'Validated specs, successful deployments increase score'],
            ['6', 'Aggregate dimensions', 'Weighted combination across all 10 dimensions'],
            ['7', 'Aggregate scope', 'Portfolio ← Program ← Team ← Service ← Feature'],
          ].map(([n, t, d]) => (
            <div key={n} style={{ display: 'grid', gridTemplateColumns: '22px 1fr', gap: '8px', padding: '7px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
              <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '10px', color: 'var(--artemy-accent)', fontWeight: 600, paddingTop: '1px' }}>{n}</div>
              <div><div style={{ fontSize: '12px', fontWeight: 500, marginBottom: '2px' }}>{t}</div><div style={{ fontSize: '11px', color: 'var(--artemy-text-secondary)' }}>{d}</div></div>
            </div>
          ))}
        </div>
        <div className="gov-card" style={{ padding: '16px' }}>
          <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '9.5px', color: 'var(--artemy-text-dim)', letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '12px' }}>Recompute Triggers</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '14px' }}>
            {['Code changes', 'Spec updates', 'Deployments', 'Incidents', 'Policy violations', 'Agent actions', 'Dependency changes', 'Security events', 'Anomaly detections', 'Approval decisions'].map(t => (
              <span key={t} style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '10px', padding: '3px 9px', borderRadius: '10px', background: 'var(--artemy-bg-elevated)', color: 'var(--artemy-text-secondary)', border: '1px solid var(--artemy-border)' }}>{t}</span>
            ))}
          </div>
          <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '9.5px', color: 'var(--artemy-text-dim)', letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '8px', marginTop: '14px' }}>Scope Levels</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {['Portfolio', 'Program', 'Product', 'Initiative', 'Team', 'Service', 'Repository', 'Specification', 'Backlog Item', 'Feature'].map(t => (
              <span key={t} style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '10px', padding: '3px 9px', borderRadius: '10px', background: 'rgba(61,127,255,.08)', color: 'var(--artemy-accent)', border: '1px solid rgba(61,127,255,.2)' }}>{t}</span>
            ))}
          </div>
        </div>
        <div className="gov-card" style={{ padding: '16px' }}>
          <div style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '9.5px', color: 'var(--artemy-text-dim)', letterSpacing: '1.2px', textTransform: 'uppercase', marginBottom: '12px' }}>Explainability Outputs</div>
          {['confidence_score', 'confidence_trend', 'risk_level', 'top_risk_factors', 'top_positive_factors', 'policy_flags', 'spec_flags', 'incident_flags', 'agent_flags', 'security_flags', 'dependency_flags'].map(f => (
            <div key={f} style={{ fontFamily: 'var(--artemy-font-mono)', fontSize: '10.5px', color: 'var(--artemy-text-secondary)', background: 'var(--artemy-bg-elevated)', padding: '5px 9px', borderRadius: '6px', marginBottom: '5px' }}>{f}</div>
          ))}
          <div style={{ marginTop: '10px', fontSize: '11px', color: 'var(--artemy-text-secondary)' }}>Every score must answer: why confidence decreased · which change introduced risk · which agent caused failure · which policy was violated · where security exposure exists</div>
        </div>
      </div>
      <div style={{ height: '40px' }}></div>
    </div>
  );
}
